import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { mockData } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { firm } = mockData;
  
  // Contact form state
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactData.name || !contactData.email || !contactData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contacts`, contactData);
      toast.success('Message sent successfully! We\'ll get back to you shortly.');
      
      // Reset form
      setContactData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Get in touch with us today. We're here to answer your questions and provide the legal guidance you need.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-2 border-slate-200 hover:border-navy-900 transition-colors duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-amber-500" />
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Phone</h3>
                <p className="text-slate-600">{firm.phone}</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-slate-200 hover:border-navy-900 transition-colors duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-amber-500" />
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Email</h3>
                <p className="text-slate-600 break-words">{firm.email}</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-slate-200 hover:border-navy-900 transition-colors duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-6 w-6 text-amber-500" />
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Address</h3>
                <p className="text-slate-600 text-sm">{firm.address}</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-slate-200 hover:border-navy-900 transition-colors duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-6 w-6 text-amber-500" />
                </div>
                <h3 className="font-bold text-navy-900 mb-2">Hours</h3>
                <p className="text-slate-600 text-sm">Mon-Fri: 9AM-5PM</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue="consultation" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 h-14">
                <TabsTrigger value="consultation" className="text-lg">Schedule Consultation</TabsTrigger>
                <TabsTrigger value="contact" className="text-lg">Send Message</TabsTrigger>
              </TabsList>
              
              <TabsContent value="consultation">
                <Card className="border-2 border-slate-200 shadow-lg">
                  <CardHeader className="bg-navy-900 text-white">
                    <CardTitle className="text-2xl">Schedule Your Consultation</CardTitle>
                    <p className="text-slate-300">Select a convenient time for your consultation using our scheduling calendar below</p>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div 
                      className="calendly-inline-widget" 
                      data-url="https://calendly.com/revealthelaw/30min"
                      style={{ minWidth: '320px', height: '700px' }}
                    ></div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="contact">
                <Card className="border-2 border-slate-200 shadow-lg">
                  <CardHeader className="bg-navy-900 text-white">
                    <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                    <p className="text-slate-300">Have a question? We're here to help</p>
                  </CardHeader>
                  <CardContent className="p-8">
                    <form onSubmit={handleContactSubmit} className="space-y-6">
                      <div>
                        <Label htmlFor="name" className="text-navy-900 font-semibold">Name *</Label>
                        <Input 
                          id="name"
                          value={contactData.name}
                          onChange={(e) => setContactData({...contactData, name: e.target.value})}
                          className="mt-2 border-slate-300 focus:border-navy-900"
                          placeholder="Your full name"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="contactEmail" className="text-navy-900 font-semibold">Email *</Label>
                          <Input 
                            id="contactEmail"
                            type="email"
                            value={contactData.email}
                            onChange={(e) => setContactData({...contactData, email: e.target.value})}
                            className="mt-2 border-slate-300 focus:border-navy-900"
                            placeholder="your.email@example.com"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="contactPhone" className="text-navy-900 font-semibold">Phone</Label>
                          <Input 
                            id="contactPhone"
                            type="tel"
                            value={contactData.phone}
                            onChange={(e) => setContactData({...contactData, phone: e.target.value})}
                            className="mt-2 border-slate-300 focus:border-navy-900"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="subject" className="text-navy-900 font-semibold">Subject</Label>
                        <Input 
                          id="subject"
                          value={contactData.subject}
                          onChange={(e) => setContactData({...contactData, subject: e.target.value})}
                          className="mt-2 border-slate-300 focus:border-navy-900"
                          placeholder="What is this regarding?"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="message" className="text-navy-900 font-semibold">Message *</Label>
                        <Textarea 
                          id="message"
                          value={contactData.message}
                          onChange={(e) => setContactData({...contactData, message: e.target.value})}
                          className="mt-2 min-h-40 border-slate-300 focus:border-navy-900"
                          placeholder="Your message..."
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6"
                        disabled={isSubmitting}
                      >
                        <Send className="mr-2 h-5 w-5" />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;